import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import StudentLayout from "../Layout";
import NetworkIndicator from "../../Practice/Components/NetworkIndicator.jsx";
import {
  useGetModuleByIdQuery,
  useGetModuleSubmissionsLazyQuery,
  useGetStudentByEmailIdQuery,
} from "../../../graphql/generated.tsx";
import Loader from "../../../components/Loader.jsx";
import { FaCalendarDay, FaTasks, FaCheckCircle, FaClock } from 'react-icons/fa';

function ChevronLeft() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      className="w-4 h-4">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      className="w-4 h-4">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

export default function DisplayTask() {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    moduleID,
    enabled_dates,
    studentId,
    moduleName,
    moduleDescription,
    moduleDomain,
    numQuestions,
    clusterId,
    campusName,
  } = location.state || {};

  const user = JSON.parse(localStorage.getItem("user"));
  const email = user?.email;

  const { data: studentData } = useGetStudentByEmailIdQuery({
    variables: { email },
    skip: !email,
  });
  const student = studentData?.getStudentByEmailId;

  const [isLoad, setIsLoad] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setIsLoad(false), 2000);
    return () => clearTimeout(t);
  }, []);

  const {
    data: moduleData,
    loading: moduleLoading,
  } = useGetModuleByIdQuery({
    variables: { id: moduleID },
    skip: !moduleID,
  });

  const [getModuleSubmissions, { data: submissionsData }] =
    useGetModuleSubmissionsLazyQuery();

  useEffect(() => {
    const moduleId =
      moduleData?.getModuleById?.id ||
      moduleID ||
      moduleData?.getModuleById?._id ||
      moduleData?.getModuleById?.moduleID;

    if (student?.id && moduleId && campusName) {
      getModuleSubmissions({
        variables: {
          studentId: String(student.id),
          moduleId,
          campusName,
        },
      });
    }
  }, [student, moduleID, moduleData, campusName, getModuleSubmissions]);

  const moduleInfo = moduleData?.getModuleById;
  const daywisePlan = moduleInfo?.daywise_plan || [];
  const allSubmissions = submissionsData?.getModuleSubmissions || [];

  

  // ── Month state — start at first enabled date ──────────────────────
  const [currentMonth, setCurrentMonth] = useState(() => {
    const firstDate = enabled_dates?.[0] ? new Date(enabled_dates[0]) : new Date();
    return new Date(firstDate.getFullYear(), firstDate.getMonth(), 1);
  });
  const hasInitMonthRef = useRef(false);

  useEffect(() => {
    if (hasInitMonthRef.current || !enabled_dates?.length) return;
    const d = new Date(enabled_dates[0]);
    if (Number.isNaN(d.getTime())) return;
    setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    hasInitMonthRef.current = true;
  }, [enabled_dates]);

  // ── Build task map ─────────────────────────────────────────────────
  const taskByDate = useMemo(() => {
    if (!enabled_dates?.length) return {};
    const map = {};
    enabled_dates.forEach((dateStr, i) => {
      const dateObj = new Date(dateStr);
      if (Number.isNaN(dateObj.getTime())) return;

      const questionsForDay = daywisePlan[i]?.questions || [];
      const questionIds = questionsForDay.map((q) =>
        typeof q === "string" ? q : q._id
      );
      const isDayCompleted =
        questionIds.length > 0 &&
        questionIds.every((qid) =>
          allSubmissions.some(
            (sub) =>
              (sub.question_id === qid || sub._id === qid) &&
              sub.is_completed === true
          )
        );

      let isEnabled = false;
      if (i === 0) {
        isEnabled = true;
      } else {
        const prevQuestions = daywisePlan[i - 1]?.questions || [];
        const prevQuestionIds = prevQuestions.map((q) =>
          typeof q === "string" ? q : q._id
        );
        const isPrevDayCompleted =
          prevQuestionIds.length > 0 &&
          prevQuestionIds.every((qid) =>
            allSubmissions.some(
              (sub) =>
                (sub.question_id === qid || sub._id === qid) &&
                sub.is_completed === true
            )
          );
        isEnabled = isPrevDayCompleted;
      }

      const key = dateObj.toISOString().slice(0, 10);
      map[key] = {
        dayIndex: i,
        dateObj,
        questionIds,
        questionCount: questionIds.length,
        isDayCompleted,
        isEnabled,
        day_id: daywisePlan[i]?.day_id,
      };
    });
    return map;
  }, [enabled_dates, daywisePlan, allSubmissions]);

  const stats = useMemo(() => {
    const tasksByDateArr = Object.values(taskByDate || {});
    const totalQuestions = tasksByDateArr.reduce((s, t) => s + (t.questionCount || 0), 0);
    const completedIds = new Set(
      (allSubmissions || [])
        .filter((s) => s.is_completed)
        .map((s) => s.question_id || s._id)
    );
    const completedQuestions = completedIds.size;
    const remainingQuestions = Math.max(0, totalQuestions - completedQuestions);
    const percent = totalQuestions ? Math.round((completedQuestions / totalQuestions) * 100) : 0;

    const totalDays = enabled_dates?.length || 0;
    const completedDays = tasksByDateArr.filter((t) => t.isDayCompleted).length;

    const todayKey = new Date().toISOString().slice(0, 10);
    const today = (taskByDate && taskByDate[todayKey]) || null;
    const todayTotal = today ? today.questionCount : 0;
    const todayCompleted = today
      ? (today.questionIds || []).filter((qid) => completedIds.has(qid)).length
      : 0;

    return {
      totalQuestions,
      completedQuestions,
      remainingQuestions,
      percent,
      totalDays,
      completedDays,
      todayTotal,
      todayCompleted,
    };
  }, [taskByDate, allSubmissions]);

  const availableTasks = useMemo(() => {
    return Object.values(taskByDate || {}).filter(
      (t) => t.isEnabled && !t.isDayCompleted
    );
  }, [taskByDate]);

  const todayKey = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const todayTask = useMemo(() => taskByDate?.[todayKey] || null, [taskByDate, todayKey]);
  const [selectedDayKey, setSelectedDayKey] = useState(null);

  function handleStartPractice() {
    if (todayTask && todayTask.isEnabled) {
      handleStart(todayTask);
      setSelectedDayKey(todayKey);
      return;
    }
    if (availableTasks.length > 0) {
      const t = availableTasks[0];
      handleStart(t);
      setSelectedDayKey(t.dateObj.toISOString().slice(0, 10));
    }
  }

  // ── Calendar cells ─────────────────────────────────────────────────
  const { monthStart, cells } = useMemo(() => {
    const ms = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      1
    );
    const me = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      0
    );
    const daysInMonth = me.getDate();
    const startOffset = (ms.getDay() + 6) % 7; // Mon = 0
    const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;

    const arr = [];
    for (let i = 0; i < totalCells; i += 1) {
      const date = new Date(ms);
      date.setDate(date.getDate() - startOffset + i);
      const inMonth = date.getMonth() === ms.getMonth();
      const key = date.toISOString().slice(0, 10);
      arr.push({ date, inMonth, key });
    }
    return { monthStart: ms, cells: arr };
  }, [currentMonth]);

  const prevMonthDate = useMemo(
    () => new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1),
    [currentMonth]
  );
  const nextMonthDate = useMemo(
    () => new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1),
    [currentMonth]
  );

  // ── Quick-link months derived from enabled_dates ───────────────────
  const quickLinkMonths = useMemo(() => {
    if (!enabled_dates?.length) return [];
    const seen = new Set();
    return enabled_dates.reduce((acc, ds) => {
      const key = ds.slice(0, 7);
      if (!seen.has(key)) {
        seen.add(key);
        const [y, m] = key.split("-").map(Number);
        acc.push({ year: y, month: m - 1, key });
      }
      return acc;
    }, []);
  }, [enabled_dates]);

  const currentMonthKey = `${currentMonth.getFullYear()}-${String(
    currentMonth.getMonth() + 1
  ).padStart(2, "0")}`;

  // ── Helpers ────────────────────────────────────────────────────────
  function cellStyle(task) {
    if (!task) return "bg-white border-gray-200";
    if (task.isDayCompleted) return "bg-green-50 border-green-200";
    if (task.isEnabled) return "bg-blue-50 border-blue-200";
    return "bg-gray-300 border-gray-300"; // locked — solid grey fill
  }

  function handleStart(task) {
    if (!task.isEnabled || !moduleInfo?.end_date) return;
    navigate("/practice-lab/display-practice-lab/module-assignment-details", {
      state: {
        moduleID,
        enabled_dates,
        dayIndex: task.dayIndex,
        questionIds: task.questionIds,
        end_date: moduleInfo.end_date,
        module: moduleInfo,
        studentId,
        day_id: task.day_id,
        clusterId,
        campusName,
        moduleName,
        moduleDescription,
        moduleDomain,
        numQuestions,
      },
    });
  }

  if (moduleLoading || isLoad) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  }

  const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <StudentLayout>
      <NetworkIndicator />

      <div className="w-full px-4 py-4 bg-white md:px-8">

        {/* Title */}
        <h2 className="mb-6 text-3xl font-extrabold leading-tight tracking-tight text-center text-blue-900">
          {moduleName || moduleInfo?.name || "Module Title"}
        </h2>

        <div className="max-w-full px-2 mx-auto md:px-6">
          <div className="flex flex-col gap-6 lg:flex-row">
            {/* Left: calendar area */}
            <div className="flex-1">
              {/* Month nav */}
              <div className="flex items-center justify-between gap-4 mb-3">
                <button
                  type="button"
                  onClick={() => setCurrentMonth(prevMonthDate)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-blue-900 transition-colors rounded-md hover:bg-blue-50"
                >
                  <ChevronLeft />
                  {prevMonthDate.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
                </button>

                <span className="text-lg font-semibold text-gray-800">
                  {monthStart.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
                </span>

                <button
                  type="button"
                  onClick={() => setCurrentMonth(nextMonthDate)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-blue-900 transition-colors rounded-md hover:bg-blue-50"
                >
                  {nextMonthDate.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
                  <ChevronRight />
                </button>
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-4 mb-4 text-xs text-gray-500">
                {[
                  { color: "bg-green-100 border-green-300", label: "Completed" },
                  { color: "bg-blue-100 border-blue-300", label: "Available" },
                  { color: "bg-gray-300 border-gray-400", label: "Locked" },
                  { color: "bg-white border-gray-200", label: "No task" },
                ].map(({ color, label }) => (
                  <div key={label} className="flex items-center gap-1.5">
                    <span className={`inline-block w-3 h-3 rounded-sm border ${color}`} />
                    {label}
                  </div>
                ))}
              </div>

              {/* Calendar table */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      {DAYS.map((d) => (
                        <th
                          key={d}
                          className="text-center text-[11px] font-semibold text-gray-400 uppercase tracking-widest pb-2"
                          style={{ width: "14.28%" }}
                        >
                          {d}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: cells.length / 7 }).map((_, row) => (
                      <tr key={row}>
                        {cells.slice(row * 7, row * 7 + 7).map((cell) => {
                          const task = taskByDate[cell.key];
                          const isMuted = !cell.inMonth;
                          const isLocked = task && !task.isEnabled && !task.isDayCompleted;

                          return (
                            <td
                              key={cell.key}
                              className={`border rounded-lg p-2 align-top h-28 ${cellStyle(task)} ${isMuted ? "opacity-30" : ""} ${selectedDayKey === cell.key ? 'ring-2 ring-blue-300' : ''}`}
                              style={{ minWidth: 0 }}
                            >
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <div className={`text-sm font-bold leading-tight ${task?.isDayCompleted ? "text-green-800" : task?.isEnabled ? "text-blue-900" : isLocked ? "text-gray-500" : "text-gray-600"}`}>
                                  {cell.date.getDate()}
                                </div>

                                {isLocked ? (
                                  <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Upcoming</span>
                                ) : task ? (
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${task.isDayCompleted ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                                    {task.isDayCompleted ? "Completed" : "Available"}
                                  </span>
                                ) : null}
                              </div>

                              {isLocked ? (
                                <div className="flex items-center justify-center h-[calc(100%-28px)]">
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500 w-7 h-7">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                  </svg>
                                </div>
                              ) : task ? (
                                <div className="flex flex-col h-[calc(100%-28px)]">
                                  <div className={`text-[15px] font-semibold leading-tight ${task.isDayCompleted ? "text-green-800" : "text-blue-900"}`}>
                                    Day {task.dayIndex + 1}
                                  </div>
                                  <div className="mt-auto">
                                    <button
                                      type="button"
                                      disabled={!task.isEnabled}
                                      onClick={() => handleStart(task)}
                                      className={`w-full text-[11px] font-semibold py-1 rounded-full ${task.isDayCompleted ? "bg-green-600 text-white cursor-default" : "bg-blue-900 text-white hover:bg-blue-800"}`}
                                    >
                                      {task.isDayCompleted ? "Completed" : "Start"}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center justify-center h-[calc(100%-28px)] text-[11px] text-gray-400">No task</div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Right: stats panel */}
            <aside className="w-full lg:w-72">
              <div className="p-4 bg-white border rounded-lg shadow-sm">
                {/* Start practicing card (compact) */}
                <div className="mb-3">
                  <div className="p-3 rounded-md bg-gray-50">
                    <div className="text-xs text-gray-500">{(todayTask && todayTask.isEnabled) || availableTasks.length > 0 ? 'Start practicing' : 'No tasks'}</div>
                    <div className="mt-1 text-sm font-medium text-gray-800">
                      {todayTask && todayTask.isEnabled ? `Day ${todayTask.dayIndex + 1}` : availableTasks[0] ? `Day ${availableTasks[0].dayIndex + 1}` : '—'}
                    </div>
                    <div className="mt-3">
                      { (todayTask && todayTask.isEnabled) || availableTasks.length > 0 ? (
                        <button
                          type="button"
                          onClick={handleStartPractice}
                          className="w-full px-3 py-2 text-sm font-semibold text-white bg-blue-900 rounded-md hover:bg-blue-800"
                        >
                          Start
                        </button>
                      ) : (
                        <button type="button" disabled className="w-full px-3 py-2 text-sm font-semibold text-gray-400 bg-white border rounded-md">No tasks</button>
                      )}
                    </div>
                  </div>
                </div>

                {/* quick-link pills removed per request */}

                <h3 className="mb-3 text-sm font-semibold text-gray-600">Progress</h3>
                <div className="mb-2">
                  <div className="text-xs text-gray-500">Days</div>
                  <div className="text-lg font-bold text-gray-900">{stats.completedDays}/{stats.totalDays}</div>
                </div>

                {/* visual progress bar for question completion with percent badge at fill end */}
                <div className="mb-4">
                  <div className="relative w-full h-2 overflow-visible bg-gray-200 rounded-full">
                    <div
                      className="h-2 transition-all bg-blue-900 rounded-full"
                      style={{ width: `${stats.percent}%` }}
                      aria-valuenow={stats.percent}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      role="progressbar"
                    />

                    <div
                      className="absolute top-0 right-0 -mt-6 text-sm font-semibold text-gray-700"
                    >
                      {stats.percent}%
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 text-gray-700 rounded-md bg-gray-50"><FaCalendarDay className="w-4 h-4" /></div>
                    <div>
                      <div className="text-xs text-gray-500">Total Days</div>
                      <div className="text-sm font-semibold">{stats.totalDays}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="p-2 text-gray-700 rounded-md bg-gray-50"><FaTasks className="w-4 h-4" /></div>
                    <div>
                      <div className="text-xs text-gray-500">Total Questions</div>
                      <div className="text-sm font-semibold">{stats.totalQuestions}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="p-2 text-green-600 rounded-md bg-green-50"><FaCheckCircle className="w-4 h-4" /></div>
                    <div>
                      <div className="text-xs text-gray-500">Completed Questions</div>
                      <div className="text-sm font-semibold">{stats.completedQuestions}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="p-2 text-yellow-600 rounded-md bg-yellow-50"><FaClock className="w-4 h-4" /></div>
                    <div>
                      <div className="text-xs text-gray-500">Remaining Questions</div>
                      <div className="text-sm font-semibold">{stats.remainingQuestions}</div>
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </StudentLayout>
  );
}